import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Grid = ({ 
    data = [], 
    columns = [], 
    add = '', 
    onEdit, 
    onDelete, 
    onView,
    onSearch,
    onStatusFilter,
    onItemsPerPageChange,
    currentPage,
    onPageChange,
    itemsPerPage,
    showSearch = true,
    showStatusFilter = true
}) => {
    const [searchValue, setSearchValue] = useState('');

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(data.length / itemsPerPage);

    // Generate page numbers
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
    }

    const handleSearchChange = (e) => {
        setSearchValue(e.target.value);
    };

    const handleSearchBlur = () => {
        if (onSearch) {
            onSearch(searchValue);
        }
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (onSearch) {
                onSearch(searchValue);
            }
        }
    };

    const handleStatusFilter = (e) => {
        const status = e.target.value;
        if (onStatusFilter) {
            onStatusFilter(status);
        }
    };

    const handleItemsPerPageChange = (e) => {
        const perPage = Number(e.target.value);
        if (onItemsPerPageChange) {
            onItemsPerPageChange(perPage);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
                    <select 
                        className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px" 
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                    </select>
                    {showSearch && (
                        <form className="navbar-search" onSubmit={e => e.preventDefault()}>
                            <input
                                type="text"
                                className="bg-base h-40-px w-auto"
                                name="search"
                                placeholder="Search by title..."
                                value={searchValue}
                                onChange={handleSearchChange}
                                onBlur={handleSearchBlur}
                                onKeyPress={handleSearchKeyPress}
                            />
                            <Icon 
                                icon="ion:search-outline" 
                                className="icon cursor-pointer" 
                                onClick={handleSearchBlur}
                            />
                        </form>
                    )}
                    {showStatusFilter && (
                        <select 
                            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                            onChange={handleStatusFilter}
                        >
                            <option value="">All Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                        </select>
                    )}
                </div>
                {add && (
                    <Link
                        to={`/${add}`}
                        className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                    >
                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                        Add New
                    </Link>
                )}
            </div>
            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col">
                                    <div className="d-flex align-items-center gap-10">
                                        <div className="form-check style-check d-flex align-items-center">
                                            <input
                                                className="form-check-input radius-4 border input-form-dark"
                                                type="checkbox"
                                                name="checkbox"
                                                id="selectAll"
                                            />
                                        </div>
                                        S.L
                                    </div>
                                </th>
                                {columns.map((column, index) => (
                                    <th key={index} scope="col" className={column.center ? "text-center" : ""}>
                                        {column.header}
                                    </th>
                                ))}
                                {(onView || onEdit || onDelete) && (
                                    <th scope="col" className="text-center">Action</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {data.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length + 2} className="text-center py-4">
                                        <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                            <Icon icon="carbon:no-data" className="text-6xl text-neutral-400 mb-3" width="64" height="64" />
                                            <p className="text-lg text-neutral-500 mb-0">No data available</p>
                                            <p className="text-sm text-neutral-400">Try adjusting your search or filter to find what you're looking for.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((item, index) => (
                                    <tr key={item.id || index}>
                                        <td>
                                            <div className="d-flex align-items-center gap-10">
                                                <div className="form-check style-check d-flex align-items-center">
                                                    <input
                                                        className="form-check-input radius-4 border border-neutral-400"
                                                        type="checkbox"
                                                        name="checkbox"
                                                    />
                                                </div>
                                                {indexOfFirstItem + index + 1}
                                            </div>
                                        </td>
                                        {columns.map((column, colIndex) => (
                                            <td key={colIndex} className={column.center ? "text-center" : ""}>
                                                {column.render ? column.render(item) : item[column.key]}
                                            </td>
                                        ))}
                                        {(onView || onEdit || onDelete) && (
                                            <td className="text-center">
                                                <div className="d-flex align-items-center gap-10 justify-content-center">
                                                    {onView && (
                                                        <button
                                                            type="button"
                                                            onClick={() => onView(item)}
                                                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        >
                                                            <Icon icon="majesticons:eye-line" className="icon text-xl" />
                                                        </button>
                                                    )}
                                                    {onEdit && (
                                                        <button
                                                            type="button"
                                                            onClick={() => onEdit(item)}
                                                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        >
                                                            <Icon icon="lucide:edit" className="menu-icon" />
                                                        </button>
                                                    )}
                                                    {onDelete && (
                                                        <button
                                                            type="button"
                                                            onClick={() => onDelete(item)}
                                                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        >
                                                            <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {data.length > 0 && (
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                        <span>
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, data.length)} of {data.length} entries
                        </span>
                        <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                    onClick={() => onPageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <Icon icon="ep:d-arrow-left" />
                                </button>
                            </li>
                            {pageNumbers.map(number => (
                                <li key={number} className="page-item">
                                    <button
                                        className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                                            currentPage === number ? 'bg-primary-600 text-white' : 'bg-neutral-200'
                                        }`}
                                        onClick={() => onPageChange(number)}
                                    >
                                        {number}
                                    </button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                    onClick={() => onPageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    <Icon icon="ep:d-arrow-right" />
                                </button>
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Grid;